<script setup lang='ts'>
import DtProjectItem from '#design-tokenomics/components/DtProjectItem.vue'
import { Button } from '@forgd/ui-kit/button'
import { InputGroup, InputGroupAddon, InputGroupInput } from '@forgd/ui-kit/input-group'
import { LoadingAnimation } from '@forgd/ui-kit/blocks/animations'
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { useQuery } from '@pinia/colada'

const emit = defineEmits(['close', 'select'])
const auth = useAuth()
const search = ref<string>('')
const { data: referenceProjects, isPending } = useQuery({
  ...buildApiQuery('/token-designer/reference-projects'),
})

const filteredProjects = computed(() =>
  referenceProjects.value?.filter(project =>
    project.name?.toLowerCase().includes(search.value.toLowerCase()),
  ) ?? [],
)

const similarProjects = computed(() =>
  referenceProjects.value?.filter(project => project.ticker === auth.project?.ticker) ?? [],
)
</script>

<template>
  <div
    class="flex h-full min-h-0 flex-col overflow-y-auto bg-white font-display"
  >
    <div
      class="sticky top-0 z-50 flex h-17.5 shrink-0 items-center justify-between border-b border-default bg-white px-5"
    >
      <div class="text-sm/normal font-semibold">
        Explore “Token Distribution Schedule” Templates
      </div>

      <div class="flex gap-2">
        <Button
          size="sm"
          variant="ghost"
          icon-only
          aria-label="Close"
          @click="emit('close')"
        >
          <XMarkIcon aria-hidden="true" />
        </Button>
      </div>
    </div>
    <div class="shrink-0 border-b border-default bg-neutral-200 p-4">
      <InputGroup>
        <InputGroupAddon class="text-slate-600">
          <MagnifyingGlassIcon class="size-4" aria-hidden="true" />
        </InputGroupAddon>
        <InputGroupInput v-model="search" placeholder="Search..." />
      </InputGroup>
    </div>
    <div v-if="isPending" class="flex h-3/4 items-center justify-center p-4">
      <LoadingAnimation />
    </div>
    <template v-else>
      <div v-if="similarProjects.length" class="flex flex-col p-2">
        <div class="border-b border-default p-4 pt-2.5 text-sm text-primal">
          Similar projects
        </div>
        <DtProjectItem v-for="project in similarProjects" :key="project.id" :project="project" @click="emit('select', project)" />
      </div>
      <div v-if="filteredProjects.length" class="flex flex-col p-2">
        <div class="border-b border-default p-4 pt-2.5 text-sm text-primal">
          All other projects
        </div>
        <DtProjectItem v-for="project in filteredProjects" :key="project.id" :project="project" @click="emit('select', project)" />
      </div>
      <div v-if="!filteredProjects.length" class="flex h-3/4 flex-col items-center justify-center p-4">
        <MagnifyingGlassIcon class="mb-2 size-10 shrink-0 text-slate-600" aria-hidden="true" />
        <div class="text-normal mb-4 flex flex-col text-center text-sm font-semibold text-muted">
          <span>Sorry!</span>
          <span>There are no results to show based on your search.</span>
        </div>
        <span class="text-normal text-sm text-muted">Please try a different query.</span>
      </div>
    </template>
  </div>
</template>
